import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import productsService from "../services/productsService";
import { STORAGE_KEYS } from "./useNavigationMemory";

// 🕒 Tiempo límite para conservar el listado de productos guardado en
// sessionStorage (usado únicamente para restaurar scroll/paginación al volver
// del detalle de un producto al catálogo). Se reinicia por completo si la
// página se recarga (ver src/main.jsx).
const PRODUCTS_STATE_VERSION = "1.0";
const PRODUCTS_STATE_TTL_MS = 10 * 60 * 1000; // 10 minutos

const useInfiniteScrollReal = (
  filters = {},
  itemsPerPage = 10,
  getApiParams = null
) => {
  // 🐛 DEBUG FLAG: Cambia a true para ver logs detallados
  const DEBUG_LOADING = false; // Logs de carga de productos
  const DEBUG_STATE = false; // Logs de guardado/restauración de estado
  const DEBUG_DUPLICATES = true; // ⭐ Logs de duplicados (SIEMPRE ACTIVO)

  // 🔍 LOG INICIAL: Ver qué recibe el hook
  if (DEBUG_LOADING) {
    console.log("🚀 useInfiniteScrollReal INVOKED with:", {
      filters,
      filtersStringified: JSON.stringify(filters),
      filtersType: typeof filters,
      filtersKeys: Object.keys(filters),
      timestamp: Date.now(),
    });
  }

  const location = useLocation();
  const containerRef = useRef(null);
  const hasRestoredScroll = useRef(false);
  const lastRequestRef = useRef(null);
  const cacheRef = useRef(new Map()); // Cache de páginas por filtros (sin uso, se conserva por compatibilidad con stats)
  const isRestoringState = useRef(false);

  // 🔧 SEPARAR ESTADO EN MÚLTIPLES useState PARA MEJOR DETECCIÓN DE CAMBIOS
  const [products, setProducts] = useState([]); // Productos mostrados
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [error, setError] = useState(null);
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false);

  // Estado interno para control
  const [_scrollPosition, _setScrollPosition] = useState(0);
  const [_startIndex, _setStartIndex] = useState(0);

  // 🚫 Ya no se cachean productos por ID de forma global (no hace falta:
  // el detalle de producto siempre pide datos frescos a la API).
  const updateGlobalCache = useCallback(() => {}, []);

  useEffect(() => {
    console.log("Filters changed, resetting state");
    console.log("products length:", products.length);
    console.log("products: ", products);
  }, [products]);

  // Generar clave única para los filtros actuales
  const getFiltersKey = useCallback((filters) => {
    const key = JSON.stringify({
      search: filters.search || "",
      category: filters.category || "Todas",
      modelo: filters.modelo || "Todas", // ✅ Corregido: era "marca" ahora es "modelo"
      priceRange: filters.priceRange || [],
      yearRange: filters.yearRange || [],
      condition: filters.condition || "Todas",
      ubicacion: filters.ubicacion || "Todas",
      features: filters.features || [],
    });
    return btoa(key).slice(0, 20); // Hash corto para la clave
  }, []);

  // � Guardar estado (para navegación con memoria: volver desde el detalle
  // de un producto restaura el scroll y los productos ya cargados). Se
  // guarda con un timestamp para que expire solo (tiempo límite) y además se
  // borra por completo si la página se recarga (ver src/main.jsx).
  const saveStateToStorage = useCallback(
    (currentFilters) => {
      try {
        // ✅ DEDUPLICAR ANTES DE GUARDAR para prevenir problemas
        const uniqueProducts = [];
        const seenIds = new Set();

        products.forEach((product) => {
          if (!seenIds.has(product.id)) {
            seenIds.add(product.id);
            uniqueProducts.push(product);
          } else if (DEBUG_DUPLICATES) {
            console.warn(
              `⚠️ DUPLICADO al guardar: ID ${product.id} - ${product.name}`
            );
          }
        });

        const stateToSave = {
          products: uniqueProducts, // ✅ GUARDAR PRODUCTOS DEDUPLICADOS
          currentPage: currentPage,
          totalProducts: totalProducts,
          hasMore: hasMore,
          timestamp: Date.now(),
          filtersKey: getFiltersKey(currentFilters),
          version: PRODUCTS_STATE_VERSION,
        };

        sessionStorage.setItem(
          STORAGE_KEYS.PRODUCTS_STATE,
          JSON.stringify(stateToSave)
        );
        sessionStorage.setItem(
          STORAGE_KEYS.FILTERS_STATE,
          JSON.stringify(currentFilters)
        );

        if (DEBUG_STATE) {
          console.log("💾 State saved to sessionStorage:", {
            originalProducts: products.length,
            uniqueProducts: uniqueProducts.length,
            duplicatesRemoved: products.length - uniqueProducts.length,
            page: currentPage,
            filters: getFiltersKey(currentFilters),
          });
        }
      } catch (error) {
        console.warn("⚠️ Failed to save state to sessionStorage:", error);
      }
    },
    [getFiltersKey, products, currentPage, totalProducts, hasMore]
  );

  // 📦 Cargar estado guardado (si sigue vigente y coincide con los filtros
  // actuales). Si expiró el tiempo límite, o cambió de versión/filtros, o la
  // página fue recargada (ver src/main.jsx), no se restaura nada y se pide
  // todo fresco a la API.
  const loadStateFromStorage = useCallback(() => {
    try {
      const savedState = sessionStorage.getItem(STORAGE_KEYS.PRODUCTS_STATE);
      const savedFilters = sessionStorage.getItem(STORAGE_KEYS.FILTERS_STATE);

      if (!savedState || !savedFilters) {
        console.log("📦 No saved state found in sessionStorage");
        return null;
      }

      const parsedState = JSON.parse(savedState);

      if (parsedState.version !== PRODUCTS_STATE_VERSION) {
        console.log("🔄 Incompatible version, not restoring state");
        sessionStorage.removeItem(STORAGE_KEYS.PRODUCTS_STATE);
        return null;
      }

      // Verificar si los filtros coinciden
      const currentFiltersKey = getFiltersKey(filters);
      if (parsedState.filtersKey !== currentFiltersKey) {
        console.log("🔄 Filters changed, not restoring state");
        return null;
      }

      // Verificar que el estado no supere el tiempo límite
      const now = Date.now();
      const stateAge = now - parsedState.timestamp;
      if (stateAge > PRODUCTS_STATE_TTL_MS) {
        console.log("⏰ Saved state is too old, not restoring");
        sessionStorage.removeItem(STORAGE_KEYS.PRODUCTS_STATE);
        return null;
      }

      console.log("✅ State loaded from sessionStorage:", {
        products: parsedState.products.length,
        page: parsedState.currentPage,
        age: `${Math.round(stateAge / 1000)}s`,
      });

      return parsedState;
    } catch (error) {
      console.warn("⚠️ Failed to load state from sessionStorage:", error);
      return null;
    }
  }, [filters, getFiltersKey]);

  // 🚫 CACHÉ DESACTIVADO: siempre se pide la página a la API para que los
  // cambios hechos por un administrador se reflejen de inmediato.
  const fetchProductsPage = useCallback(
    async (page, currentFilters) => {
      console.log(
        `🌐 Fetching page ${page} from API with filters:`,
        currentFilters
      );

      try {
        // Preparar parámetros base de la API
        const baseParams = {
          page,
          limit: itemsPerPage,
          featured: false,
          area: 1, // ✅ NUEVO: Filtrar por área "Bienes" por defecto
        };

        // Usar la función getApiParams si está disponible, sino construir manualmente
        let apiParams;
        if (getApiParams && typeof getApiParams === "function") {
          apiParams = { ...baseParams, ...getApiParams(baseParams) };
          console.log(
            "🎯 Using getApiParams from useServerFilters:",
            apiParams
          );
        } else {
          // Fallback: construcción manual (para compatibilidad)
          apiParams = { ...baseParams };

          // ✅ ACTUALIZADO: Mapear filtros para API
          if (currentFilters.search && currentFilters.search.trim()) {
            apiParams.search = currentFilters.search.trim();
          }
          if (currentFilters.category && currentFilters.category !== "Todas") {
            apiParams.category = currentFilters.category;
          }
          if (currentFilters.modelo && currentFilters.modelo !== "Todas") {
            apiParams.model = currentFilters.modelo; // ✅ Frontend: "modelo" -> API: "model"
          }
          if (
            currentFilters.condition &&
            currentFilters.condition !== "Todas"
          ) {
            apiParams.condition = currentFilters.condition;
          }
          if (
            currentFilters.ubicacion &&
            currentFilters.ubicacion !== "Todas"
          ) {
            apiParams.location = currentFilters.ubicacion; // ✅ Frontend: "ubicacion" -> API: "location"
          }
          if (
            currentFilters.priceRange &&
            currentFilters.priceRange.length === 2
          ) {
            apiParams.priceMin = currentFilters.priceRange[0]; // ✅ API: priceMin
            apiParams.priceMax = currentFilters.priceRange[1]; // ✅ API: priceMax
          }
          if (
            currentFilters.yearRange &&
            currentFilters.yearRange.length === 2
          ) {
            apiParams.yearMin = currentFilters.yearRange[0]; // ✅ API: yearMin
            apiParams.yearMax = currentFilters.yearRange[1]; // ✅ API: yearMax
          }
          if (currentFilters.features && currentFilters.features.length > 0) {
            apiParams.features = currentFilters.features;
          }

          console.log("⚠️ Using manual filter construction:", apiParams);
        }

        const response = await productsService.getProducts(apiParams);

        if (response && response.products) {
          // ✅ NUEVO: Verificar hasNextPage de la API
          const pagination = {
            total: response.total,
            hasNextPage: response.hasNextPage,
            totalPages: response.totalPages,
            page: response.page,
            limit: response.limit,
          };

          const processedResponse = {
            products: response.products,
            pagination: pagination,
          };

          // 🚫 CACHÉ DESACTIVADO: ya no se guarda la página en memoria

          // Actualizar cache global
          updateGlobalCache(response.products);

          console.log(`✅ Page ${page} loaded and cached:`, {
            products: response.products.length,
            total: pagination.total,
            hasNextPage: pagination.hasNextPage,
            apiParams,
          });

          return processedResponse;
        } else {
          throw new Error("No products received from API");
        }
      } catch (error) {
        console.error(`❌ Error fetching page ${page} from API:`, error);
        throw error;
      }
    },
    [updateGlobalCache, getApiParams, itemsPerPage]
  ); // ✅ CORRECCIÓN: Agregar itemsPerPage a las dependencias

  // ✅ ACTUALIZADO: Cargar más productos con lógica optimizada
  const loadMoreProducts = useCallback(async () => {
    if (loading || !hasMore || isRestoringState.current) return;

    // Cancelar request anterior si existe
    if (lastRequestRef.current) {
      lastRequestRef.current.cancelled = true;
    }

    const requestId = { cancelled: false };
    lastRequestRef.current = requestId;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchProductsPage(currentPage, filters);

      // Verificar si el request fue cancelado
      if (requestId.cancelled) {
        console.log("🚫 Request cancelled");
        return;
      }

      const newProducts = response.products || [];
      const pagination = response.pagination || {};

      console.log("🔧 BEFORE UPDATE - Current state:", {
        currentPage,
        currentProductsLength: products.length,
        newProductsLength: newProducts.length,
        hasNextPage: pagination.hasNextPage,
      });

      // Actualizar productos
      setProducts((prevProducts) => {
        // ✅ FIX DUPLICADOS: Crear un Set de IDs existentes para deduplicación
        const existingIds = new Set(prevProducts.map((p) => p.id));

        // Filtrar productos nuevos que NO existan ya
        const uniqueNewProducts = newProducts.filter((product) => {
          if (existingIds.has(product.id)) {
            console.warn(
              `⚠️ Producto duplicado detectado y eliminado: ID ${product.id} - ${product.name}`
            );
            return false;
          }
          return true;
        });

        const updatedProducts =
          currentPage === 1
            ? newProducts // Primera carga - usar todos los productos
            : [...prevProducts, ...uniqueNewProducts]; // ✅ CORREGIDO: Solo agregar productos únicos

        console.log("🔧 PRODUCTS UPDATE:", {
          previousLength: prevProducts.length,
          newProductsLength: newProducts.length,
          uniqueNewProductsLength: uniqueNewProducts.length,
          duplicatesRemoved: newProducts.length - uniqueNewProducts.length,
          finalLength: updatedProducts.length,
          currentPage,
          isFirstPage: currentPage === 1,
        });

        return updatedProducts;
      });

      // Actualizar otros estados
      setLoading(false);
      setInitialLoading(false);
      setCurrentPage((prevPage) => prevPage + 1);

      // ✅ ACTUALIZADO: Usar hasNextPage directo de la API
      setHasMore(pagination.hasNextPage === true);
      setTotalProducts(pagination.total || 0);

      console.log(`📊 Products loaded:`, {
        page: currentPage,
        newProducts: newProducts.length,
        totalLoaded:
          (currentPage === 1 ? 0 : products.length) + newProducts.length,
        hasNextPage: pagination.hasNextPage,
        totalProducts: pagination.total,
        // ✅ NUEVO: Información adicional para debugging
        previousProducts: products.length,
        uniqueNewProducts:
          currentPage === 1
            ? newProducts.length
            : newProducts.filter((p) => !products.some((ep) => ep.id === p.id))
                .length,
        expectedTotal: pagination.total,
        willHaveMore: pagination.hasNextPage,
      });
    } catch (error) {
      if (!requestId.cancelled) {
        setLoading(false);
        setInitialLoading(false);
        setError(error.message);
      }
    }
  }, [
    loading,
    hasMore,
    currentPage,
    fetchProductsPage,
    filters,
    products.length,
  ]); // ✅ CORRECCIÓN: Remover itemsPerPage ya que no se usa directamente

  // Reset cuando cambien los filtros
  const resetProducts = useCallback(() => {
    console.log("🔄 Resetting products due to filter change");

    // Cancelar requests pendientes
    if (lastRequestRef.current) {
      lastRequestRef.current.cancelled = true;
    }

    // Limpiar estado guardado
    sessionStorage.removeItem(STORAGE_KEYS.PRODUCTS_STATE);
    sessionStorage.removeItem(STORAGE_KEYS.SCROLL_POSITION);

    setProducts([]);
    setLoading(false);
    setInitialLoading(true);
    setHasMore(true);
    setCurrentPage(1);
    setTotalProducts(0);
    setError(null);
    setShouldRestoreScroll(false);

    hasRestoredScroll.current = false;
    isRestoringState.current = false;

    // Scroll suave al top
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Detectar cambios en filtros - SIMPLIFICADO para mejor debugging
  const filtersKey = useMemo(() => {
    const key = getFiltersKey(filters);
    console.log(
      "🔧 useMemo filtersKey calculated:",
      key,
      "from filters:",
      filters
    );
    return key;
  }, [filters, getFiltersKey]);

  const prevFiltersKeyRef = useRef(filtersKey);

  // 🎯 EFECTO SIMPLIFICADO: Solo detectar cambios en filtersKey
  useEffect(() => {
    console.log("🔍 useInfiniteScrollReal - EFFECT TRIGGERED:", {
      currentFiltersKey: filtersKey,
      previousFiltersKey: prevFiltersKeyRef.current,
      filtersChanged: prevFiltersKeyRef.current !== filtersKey,
      currentFilters: filters,
      timestamp: Date.now(),
    });

    if (prevFiltersKeyRef.current !== filtersKey) {
      console.log(
        "🎯 useInfiniteScrollReal - FILTERS CHANGED! Calling resetProducts..."
      );
      prevFiltersKeyRef.current = filtersKey;
      resetProducts();
    } else {
      console.log("⚪ useInfiniteScrollReal - No filter changes detected");
    }
  }, [filtersKey]); // 🔧 SOLO depender de filtersKey, no de resetProducts

  // 🆕 EFECTO ADICIONAL: Solo detectar cambios en filtersKey
  useEffect(() => {
    console.log("🔍 useInfiniteScrollReal - FILTERS EFFECT TRIGGERED:", {
      filters,
      filtersStringified: JSON.stringify(filters),
      timestamp: Date.now(),
    });

    // Si este es el primer render o si los filtros no están vacíos, verificar cambios
    if (Object.keys(filters).length > 0) {
      const currentKey = getFiltersKey(filters);
      console.log(
        "🔧 Current filters key:",
        currentKey,
        "Previous key:",
        prevFiltersKeyRef.current
      );

      if (
        prevFiltersKeyRef.current &&
        prevFiltersKeyRef.current !== currentKey
      ) {
        console.log(
          "🎯 useInfiniteScrollReal - BACKUP DETECTION: Filters changed!"
        );
        prevFiltersKeyRef.current = currentKey;
        resetProducts();
      } else if (!prevFiltersKeyRef.current) {
        // Primera vez, solo guardar la referencia
        prevFiltersKeyRef.current = currentKey;
        console.log(
          "🏁 useInfiniteScrollReal - Initial filters key set:",
          currentKey
        );

        // Si no es la configuración inicial por defecto, cargar productos inmediatamente
        const hasActiveFilters = Object.entries(filters).some(
          ([key, value]) => {
            if (key === "search" && value && value.trim()) return true;
            if (typeof value === "string" && value !== "" && value !== "Todas")
              return true;
            if (Array.isArray(value) && value.length > 0) return true;
            return false;
          }
        );

        if (hasActiveFilters) {
          console.log(
            "🎯 useInfiniteScrollReal - Active filters detected on initial load, resetting products"
          );
          resetProducts();
        }
      }
    }
  }, [filters, getFiltersKey, resetProducts]); // Agregar getFiltersKey y resetProducts a las dependencias

  // 🆕 EFECTO: Escuchar evento personalizado de filtros aplicados
  useEffect(() => {
    const handleFiltersApplied = (event) => {
      console.log(
        "🎯 useInfiniteScrollReal - FILTERS APPLIED EVENT RECEIVED:",
        event.detail
      );

      // 🔥 LIMPIAR CACHE COMPLETO para forzar nuevas consultas
      console.log("🧹 Clearing ALL cache due to filters applied");
      cacheRef.current.clear();
      sessionStorage.removeItem(STORAGE_KEYS.PRODUCTS_STATE);
      sessionStorage.removeItem(STORAGE_KEYS.SCROLL_POSITION);

      console.log("🔄 Resetting products due to filters applied event");
      resetProducts();
    };

    // Escuchar el evento personalizado
    window.addEventListener("filtersApplied", handleFiltersApplied);

    // Cleanup
    return () => {
      window.removeEventListener("filtersApplied", handleFiltersApplied);
    };
  }, [resetProducts]);

  // 🆕 EFECTO: Guardar estado cuando se actualicen los productos
  useEffect(() => {
    // Solo guardar si no estamos en loading inicial o restaurando estado
    if (!initialLoading && !isRestoringState.current && products.length > 0) {
      console.log("💾 Auto-saving state after products update:", {
        productsLength: products.length,
        currentPage,
        filtersKey: getFiltersKey(filters),
      });
      saveStateToStorage(filters);
    }
  }, [
    products.length,
    currentPage,
    initialLoading,
    saveStateToStorage,
    filters,
    getFiltersKey,
  ]);

  // Cargar productos iniciales o restaurar estado
  useEffect(() => {
    if (initialLoading && products.length === 0 && !isRestoringState.current) {
      // Intentar restaurar estado primero
      const savedState = loadStateFromStorage();

      if (savedState && savedState.products.length > 0) {
        console.log("🔄 Restoring saved state...");
        isRestoringState.current = true;

        // ✅ FIX: Deduplicar productos ANTES de restaurar
        const uniqueProducts = [];
        const seenIds = new Set();

        savedState.products.forEach((product) => {
          if (!seenIds.has(product.id)) {
            seenIds.add(product.id);
            uniqueProducts.push(product);
          } else if (DEBUG_DUPLICATES) {
            console.warn(
              `⚠️ DUPLICADO al restaurar: ID ${product.id} - ${product.name}`
            );
          }
        });

        if (
          DEBUG_DUPLICATES &&
          savedState.products.length !== uniqueProducts.length
        ) {
          console.warn(
            `🚨 ${
              savedState.products.length - uniqueProducts.length
            } duplicados eliminados al restaurar desde caché`
          );
        }

        console.log("🔧 Estado restaurado:", {
          productosOriginales: savedState.products.length,
          productosUnicos: uniqueProducts.length,
          duplicadosEliminados:
            savedState.products.length - uniqueProducts.length,
        });

        setProducts(uniqueProducts); // ✅ USAR PRODUCTOS DEDUPLICADOS
        setLoading(false);
        setInitialLoading(false);
        setHasMore(savedState.hasMore);
        setCurrentPage(savedState.currentPage);
        setTotalProducts(savedState.totalProducts);
        setError(null);
        setShouldRestoreScroll(true);

        // ✅ IMPORTANTE: Mantener el flag durante un tiempo para evitar race conditions
        setTimeout(() => {
          isRestoringState.current = false;
          console.log(
            "✅ Restauración completada, hook listo para cargar más productos"
          );
        }, 500); // Esperar 500ms antes de permitir nuevas cargas
      } else {
        // No hay estado guardado, cargar normalmente
        loadMoreProducts();
      }
    }
  }, [initialLoading, products.length, loadMoreProducts, loadStateFromStorage]);

  // Efecto separado para restaurar scroll después de que los productos se hayan renderizado
  useEffect(() => {
    if (
      shouldRestoreScroll &&
      products.length > 0 &&
      !hasRestoredScroll.current
    ) {
      const savedScrollPosition = sessionStorage.getItem(
        STORAGE_KEYS.SCROLL_POSITION
      );

      if (savedScrollPosition) {
        const position = parseInt(savedScrollPosition);

        // Función para intentar restaurar el scroll
        const attemptScrollRestore = (attempt = 1, maxAttempts = 10) => {
          const documentHeight = document.documentElement.scrollHeight;
          const viewportHeight = window.innerHeight;
          const maxScrollPosition = documentHeight - viewportHeight;

          console.log(`🔄 Attempt ${attempt} to restore scroll:`, {
            targetPosition: position,
            documentHeight,
            maxScrollPosition,
            currentProducts: products.length,
          });

          // Verificar si el documento tiene suficiente altura para el scroll
          if (position <= maxScrollPosition || attempt >= maxAttempts) {
            // Restaurar el scroll
            window.scrollTo({
              top: Math.min(position, maxScrollPosition),
              behavior: "auto",
            });

            hasRestoredScroll.current = true;
            sessionStorage.removeItem(STORAGE_KEYS.SCROLL_POSITION);

            // Actualizar estado para no intentar restaurar de nuevo
            setShouldRestoreScroll(false);

            console.log(
              `✅ Scroll restored to position: ${Math.min(
                position,
                maxScrollPosition
              )}`
            );
          } else {
            // Esperar un poco más para que se rendericen más productos
            setTimeout(() => {
              attemptScrollRestore(attempt + 1, maxAttempts);
            }, 200);
          }
        };

        // Esperar un frame para que React termine de renderizar
        requestAnimationFrame(() => {
          setTimeout(() => {
            attemptScrollRestore();
          }, 100);
        });
      } else {
        // No hay posición guardada, simplemente marcar como restaurado
        setShouldRestoreScroll(false);
      }
    }
  }, [shouldRestoreScroll, products.length]);

  // Observer para scroll infinito
  useEffect(() => {
    // ✅ FIX: No crear observer si estamos restaurando estado
    // if (isRestoringState.current || shouldRestoreScroll) {
    //   console.log("⏸️ Observer creation skipped - restoring state");
    //   return;
    // }

    const observer = new IntersectionObserver(
      (entries) => {
        // ✅ DOBLE VERIFICACIÓN: Verificar flags antes de cargar
        if (isRestoringState.current || shouldRestoreScroll) {
          console.log("⏸️ Observer triggered but blocked - restoring state");
          return;
        }

        if (
          entries[0].isIntersecting &&
          !loading &&
          hasMore &&
          !initialLoading
        ) {
          console.log("📡 Observer triggered - loading more products");
          loadMoreProducts();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "200px", // Cargar antes de llegar al final
      }
    );

    const sentinel = document.getElementById("scroll-sentinel");
    if (sentinel) {
      observer.observe(sentinel);
      console.log("👁️ Observer connected to sentinel");
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
        console.log("👁️ Observer disconnected from sentinel");
      }
    };
  }, [
    loadMoreProducts,
    loading,
    hasMore,
    initialLoading,
    shouldRestoreScroll,
    isRestoringState.current,
  ]); // ✅ Agregar shouldRestoreScroll como dependencia

  // Función para guardar posición de scroll al navegar
  const saveScrollPosition = useCallback(() => {
    const scrollY = window.scrollY;
    if (scrollY > 0) {
      sessionStorage.setItem(STORAGE_KEYS.SCROLL_POSITION, scrollY.toString());
      console.log(`💾 Scroll position saved: ${scrollY}`);
    }
  }, []);

  // Reset scroll flag cuando cambie la ubicación
  useEffect(() => {
    hasRestoredScroll.current = false;
  }, [location.pathname]);

  // Limpiar cache cuando el componente se desmonte
  useEffect(() => {
    const currentCache = cacheRef.current;

    return () => {
      // Limpiar cache viejo (más de 30 minutos)
      const now = Date.now();
      const maxAge = 30 * 60 * 1000;

      for (const [key, value] of currentCache.entries()) {
        if (now - value.timestamp > maxAge) {
          currentCache.delete(key);
        }
      }
    };
  }, []);

  return {
    // Datos
    products,
    totalProducts,

    // Estados
    loading,
    initialLoading,
    hasMore,
    error,

    // Funciones
    loadMoreProducts,
    resetProducts,
    saveScrollPosition,

    // Referencias
    containerRef,

    // Stats para debugging
    stats: {
      displayed: products.length,
      total: totalProducts,
      currentPage,
      hasMore,
      loading,
      cacheSize: cacheRef.current.size,
      filtersKey,
      isRestoring: isRestoringState.current,
    },
  };
};

export default useInfiniteScrollReal;
